import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AnimateHeight from "react-animate-height";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { useMinWidth } from "../../hooks/media-query";
import { Roles } from "../roles";
import AppIcon from "../AppIcon";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/DropdownMenu";
import type { RootState } from "../../store/redux/store/store";

export type NavbarProps = {
  logo?: string;
  items?: NavbarItemProps[];
  sideItems?: NavbarItemProps[];
  primaryColor?: string;
  role?: any; // kept for backward compat — ignored, read from Redux instead
};

export type NavbarItemProps = {
  id: string;
  to?: string;
  content: React.JSX.Element | string;
  menu?: any[];
  onClick?: () => void;
  primaryColor?: string;
  roles?: any; // role-based visibility
};

export function NavbarItem({ id, to, content, menu, onClick, selected }: NavbarItemProps & { selected?: boolean }) {
  if (to || !menu || menu.length === 0) {
    return (
      <div
        key={"NavBarItemBox-" + id}
        className={
          "relative inline-flex items-center px-1 py-1.5 cursor-pointer whitespace-nowrap font-medium text-[var(--mowazaf-text,#1a2b49)] transition-colors border-b-2 border-transparent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" +
          (selected ? " text-primary font-semibold !border-primary" : "")
        }
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
        role="tab"
        aria-selected={selected}
        tabIndex={0}
      >
        {content}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          key={"NavBarItemMenuButton-" + id}
          className={
            "relative inline-flex items-center px-1 py-1.5 cursor-pointer whitespace-nowrap font-medium text-[var(--mowazaf-text,#1a2b49)] transition-colors border-b-2 border-transparent hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" +
            (selected ? " text-primary font-semibold !border-primary" : "")
          }
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
          role="tab"
          aria-selected={selected}
          tabIndex={0}
        >
          {content}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {menu.map((item, index) => (
          <DropdownMenuItem
            key={`menu-item-${index}`}
            onSelect={(ev: any) => {
              if (item.onClick) item.onClick(ev);
            }}
          >
            {item.children}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ProfileInnerNavbar({ items, sideItems, primaryColor }: NavbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const lg = useMinWidth("1200");
  const [expended, setExpended] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const role = useSelector((state: RootState) => state.user?.role);

  const navigate = useNavigate();
  const navbarItemCallback = (item: NavbarItemProps, i?: number) => () => {
    if (item.onClick) {
      item.onClick();
    }
    if (item.to) {
      navigate(item.to);
    }
    setExpended(false);
    if (i !== undefined) {
      setSelectedIndex(i);
    } else {
      setSelectedIndex(0);
    }
  };

  // Filter items & side items by role if roles specified on item
  const filteredItems = items?.filter(it => (it.roles ?? Roles.ANY).includes(role)) || [];
  const filteredSideItems = sideItems?.filter(it => (it.roles ?? Roles.ANY).includes(role)) || [];

  // Derive active tab from location. Match the item's own route first: ids are camelCase
  // ("variablePay") while routes are kebab-case ("/variable-pay"), so the id-only match left those
  // tabs highlighting the first tab. The index must be into `filteredItems` — that is what the tab
  // strip renders and compares `selectedIndex` against — or a role-hidden earlier tab shifts it.
  useEffect(() => {
    const byRoute = filteredItems.findIndex(it =>
      !!it.to && (currentPath === it.to || currentPath.startsWith(`${it.to}/`)));
    const idx = byRoute >= 0 ? byRoute : filteredItems.findIndex(it => !!it.id && currentPath.includes(it.id));
    if (idx >= 0 && idx !== selectedIndex) setSelectedIndex(idx);
  }, [currentPath, filteredItems, selectedIndex]);

  if (lg) {
    return (
      <nav className="bg-card border-b border-border rounded-lg text-sm" role="tablist">
        <div className="flex items-stretch gap-4 px-4 pt-2 pb-1 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
          {filteredItems.map((item, i) => {
            if (item.menu && item.menu.length > 0) {
              item.menu = item.menu.map((subItem: any, subI) => {
                const subItemNav: NavbarItemProps = { ...subItem.variables };
                subI = i + (subI / 10);
                subItem.onClick = () => { navbarItemCallback(subItemNav, i)(); };
                return subItem;
              });
            }
            return (
              <NavbarItem
                key={"NavbarItem-" + item.id}
                {...item}
                onClick={navbarItemCallback(item, i)}
                selected={selectedIndex === i}
                primaryColor={primaryColor}
              />
            );
          })}
          <div className="flex-1" />
          {filteredSideItems.map((item) => (
            <NavbarItem key={"NavbarItem-" + item.id} {...item} onClick={navbarItemCallback(item)} primaryColor={primaryColor} />
          ))}
        </div>
      </nav>
    );
  }

  return (
    <div className="bg-card border-b border-border px-2" role="tablist">
      <div className="flex justify-end">
        <button
          onClick={() => setExpended(!expended)}
          aria-expanded={expended}
          aria-label={t('Toggle payroll navigation')}
          className="p-2 rounded hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {expended ? <AppIcon name="X" size={20} /> : <AppIcon name="Menu" size={20} />}
        </button>
      </div>
      <AnimateHeight height={expended ? "auto" : 0}>
        <div className="flex flex-col gap-0.5 py-1 pb-2">
          {filteredItems.map((item, i) => (
            <NavbarItem
              key={item.id}
              {...item}
              onClick={navbarItemCallback(item, i)}
              selected={i === selectedIndex}
              primaryColor={primaryColor}
            />
          ))}
          {filteredSideItems.map((item) => (
            <NavbarItem
              key={item.id}
              {...item}
              onClick={navbarItemCallback(item)}
              primaryColor={primaryColor}
            />
          ))}
        </div>
      </AnimateHeight>
    </div>
  );
}

export default ProfileInnerNavbar;
